import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { PaymentSetting, PaymentSettingService } from "../../../services/payment-setting.service";
import {
  getStripeMinMajorAmount,
  isStripeZeroDecimalCurrency,
  stripePriceErrorMessage,
  validateStripeCurrency,
  validateStripeMajorAmount,
} from "../../../utils/stripe-price.util";

@Component({
  selector: "app-tab-renewal-pricing",
  templateUrl: "./tab-renewal-pricing.component.html",
  styleUrls: ["./tab-renewal-pricing.component.scss"],
})
export class TabRenewalPricingComponent implements OnInit, OnChanges {
  @Input() model: any = {};
  @Input() digitalLogId: number | string;
  @Input() isEditMode: boolean = false;
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;

  @Output() modelChange = new EventEmitter<any>();

  loading = false;
  loadError: string | null = null;
  private paymentSettingId: number | null = null;
  private loadingRequestId = 0;

  constructor(private paymentSettingService: PaymentSettingService) {}

  ngOnInit(): void {
    this.loadPaymentSetting();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const digitalLogIdChanged = !!(changes["digitalLogId"] && !changes["digitalLogId"].firstChange);
    if (digitalLogIdChanged) {
      this.loadPaymentSetting();
      return;
    }

    const modelChange = changes["model"];
    if (
      !modelChange ||
      modelChange.firstChange ||
      modelChange.previousValue === modelChange.currentValue
    ) {
      return;
    }

    // Parent onModelChange spreads into a new object on every keystroke.
    // Only re-fetch when the new model has no UI pricing fields (e.g. after publish refresh).
    if (this.isMissingUiPricing(modelChange.currentValue)) {
      this.loadPaymentSetting();
    }
  }

  private isMissingUiPricing(model: any): boolean {
    if (!model) {
      return true;
    }
    const noMonthly = model.renewalMonthlyPrice == null || model.renewalMonthlyPrice === "";
    const noYearly = model.renewalYearlyPrice == null || model.renewalYearlyPrice === "";
    const noCurrency = !model.renewalCurrency || String(model.renewalCurrency).trim().length === 0;
    return noMonthly && noYearly && noCurrency;
  }

  onFieldChange(): void {
    this.modelChange.emit(this.model);
  }

  autoCheck(field: string): void {
    this.onFieldChange();
  }

  /** Re-apply PaymentSetting after parent refreshes the DML model. */
  reloadPaymentSetting(): void {
    this.loadPaymentSetting();
  }

  getPaymentSettingId(): number | null {
    return this.paymentSettingId;
  }

  syncFromSavedSetting(setting: PaymentSetting): void {
    if (!setting) {
      return;
    }
    this.model.paymentSetting = setting;
    this.applyPaymentSetting(setting);
  }

  get priceStep(): string {
    return isStripeZeroDecimalCurrency(this.model && this.model.renewalCurrency) ? "1" : "0.01";
  }

  get priceMin(): number {
    return getStripeMinMajorAmount(this.model && this.model.renewalCurrency);
  }

  get monthlyPriceError(): string {
    const err = validateStripeMajorAmount(
      this.model && this.model.renewalMonthlyPrice,
      this.model && this.model.renewalCurrency
    );
    return stripePriceErrorMessage(err, this.model && this.model.renewalCurrency, "Monthly");
  }

  get yearlyPriceError(): string {
    const err = validateStripeMajorAmount(
      this.model && this.model.renewalYearlyPrice,
      this.model && this.model.renewalCurrency
    );
    return stripePriceErrorMessage(err, this.model && this.model.renewalCurrency, "Yearly");
  }

  get currencyError(): string {
    return validateStripeCurrency(this.model && this.model.renewalCurrency) || "";
  }

  isMonthlyInvalid(): boolean {
    return !!validateStripeMajorAmount(
      this.model && this.model.renewalMonthlyPrice,
      this.model && this.model.renewalCurrency
    );
  }

  isYearlyInvalid(): boolean {
    return !!validateStripeMajorAmount(
      this.model && this.model.renewalYearlyPrice,
      this.model && this.model.renewalCurrency
    );
  }

  isCurrencyInvalid(): boolean {
    return !!validateStripeCurrency(this.model && this.model.renewalCurrency);
  }

  validateRenewalPricing(): boolean {
    return !this.isMonthlyInvalid() && !this.isYearlyInvalid() && !this.isCurrencyInvalid();
  }

  savePaymentSetting(): Promise<void> {
    const digitalMemoryLogId = Number(this.digitalLogId);
    if (!digitalMemoryLogId || !Number.isFinite(digitalMemoryLogId)) {
      return Promise.reject(new Error("Digital Memory Log id is required to save payment settings."));
    }

    if (!this.validateRenewalPricing()) {
      return Promise.reject(new Error("Renewal pricing is invalid."));
    }

    const nestedId =
      this.model && this.model.paymentSetting && this.model.paymentSetting.id
        ? this.model.paymentSetting.id
        : null;
    const settingId = this.paymentSettingId || nestedId;

    const payload: PaymentSetting = {
      digitalMemoryLogId: digitalMemoryLogId,
      monthlyPrice: this.paymentSettingService.toSmallestUnit(
        this.model.renewalMonthlyPrice,
        this.model.renewalCurrency
      ),
      annualPrice: this.paymentSettingService.toSmallestUnit(
        this.model.renewalYearlyPrice,
        this.model.renewalCurrency
      ),
      currency: String(this.model.renewalCurrency)
        .trim()
        .toLowerCase(),
      active: true,
    };

    const request$ = settingId
      ? this.paymentSettingService.update(settingId, payload)
      : this.paymentSettingService.create(payload);

    return request$
      .toPromise()
      .then((saved: PaymentSetting) => {
        if (saved && saved.id) {
          this.paymentSettingId = saved.id;
        }
        this.model.paymentSetting = saved || Object.assign({}, payload, { id: this.paymentSettingId });
      });
  }

  private loadPaymentSetting(): void {
    if (!this.model) {
      return;
    }

    this.loadError = null;

    const nested = this.model.paymentSetting;
    if (this.hasPricedPaymentSetting(nested)) {
      this.loading = false;
      this.applyPaymentSetting(nested);
      return;
    }

    if (nested && nested.id) {
      this.paymentSettingId = nested.id;
    }

    // Create/draft has a DML id but no PaymentSetting yet — skip GET to avoid 404.
    if (!this.isEditMode) {
      this.applyDefaultsIfNeeded();
      return;
    }

    const digitalMemoryLogId = Number(this.digitalLogId);
    if (!digitalMemoryLogId || !Number.isFinite(digitalMemoryLogId)) {
      this.applyDefaultsIfNeeded();
      return;
    }

    const requestId = ++this.loadingRequestId;
    this.loading = true;

    this.paymentSettingService.getByDigitalMemoryLogId(digitalMemoryLogId).subscribe(
      (setting: PaymentSetting | null) => {
        if (requestId !== this.loadingRequestId || !this.model) {
          return;
        }

        this.loading = false;

        if (setting) {
          this.model.paymentSetting = setting;
          this.applyPaymentSetting(setting);
          return;
        }

        this.paymentSettingId = null;
        this.applyDefaultsIfNeeded();
      },
      () => {
        if (requestId !== this.loadingRequestId) {
          return;
        }

        this.loading = false;
        this.loadError = "Unable to load payment settings.";
        this.paymentSettingId = null;
        this.applyDefaultsIfNeeded();
      }
    );
  }

  private hasPricedPaymentSetting(setting: any): boolean {
    if (!setting) {
      return false;
    }
    const monthly = Number(setting.monthlyPrice);
    const annual = Number(setting.annualPrice);
    return (Number.isFinite(monthly) && monthly > 0) || (Number.isFinite(annual) && annual > 0);
  }

  private applyPaymentSetting(setting: PaymentSetting): void {
    this.paymentSettingId = setting.id || null;

    const monthly = this.paymentSettingService.fromSmallestUnit(
      setting.monthlyPrice,
      setting.currency
    );
    const annual = this.paymentSettingService.fromSmallestUnit(
      setting.annualPrice,
      setting.currency
    );

    this.model.renewalMonthlyPrice = monthly != null && monthly > 0 ? monthly : null;
    this.model.renewalYearlyPrice = annual != null && annual > 0 ? annual : null;

    if (setting.currency && String(setting.currency).trim()) {
      this.model.renewalCurrency = String(setting.currency)
        .trim()
        .toUpperCase();
    } else {
      this.model.renewalCurrency = "";
    }
  }

  private applyDefaultsIfNeeded(): void {
    if (this.model.renewalMonthlyPrice == null || this.model.renewalMonthlyPrice === "") {
      this.model.renewalMonthlyPrice = null;
    }

    if (this.model.renewalYearlyPrice == null || this.model.renewalYearlyPrice === "") {
      this.model.renewalYearlyPrice = null;
    }

    if (!this.model.renewalCurrency) {
      this.model.renewalCurrency = "";
    }
  }
}
